import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Eye, Upload } from 'lucide-react';
import { authorAuthService, AuthorDashboardStats, SubmissionItem } from '../services/authorAuth';
import { useAuth } from '../../../auth/hooks/auth';
import ReusableTable, { TableColumn, TableAction } from '../../admin/components/adminTable';
import Drawer from '../../sharedComponents/Drawer';
import SubmissionDetails from './SubmissionDetails';
import RevisedCopyDetails from './RevisedCopyDetails';

const getVisiblePageNumbers = (currentPage: number, totalPages: number): Array<number | 'ellipsis'> => {
    if (totalPages <= 5) return Array.from({ length: totalPages }, (_, index) => index + 1);

    const pages: Array<number | 'ellipsis'> = [1];
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);

    if (start > 2) pages.push('ellipsis');
    for (let page = start; page <= end; page += 1) pages.push(page);
    if (end < totalPages - 1) pages.push('ellipsis');
    pages.push(totalPages);

    return pages;
};

const Submissions: React.FC = () => {
    const { user } = useAuth();
    const [submissions, setSubmissions] = useState<SubmissionItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [selectedSubmission, setSelectedSubmission] = useState<SubmissionItem | null>(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [activeCycleId, setActiveCycleId] = useState<number | null>(null);

    const [activeTab, setActiveTab] = useState<'my' | 'returned' >('my');

    // Returned Submissions resubmit drawer
    const [isReturnedDrawerOpen, setIsReturnedDrawerOpen] = useState(false);
    const [selectedReturnedSubmission, setSelectedReturnedSubmission] = useState<any>(null);

    // Returned Submissions tab state
    const [returnedSubmissions, setReturnedSubmissions] = useState<SubmissionItem[]>([]);
    const [isLoadingReturned, setIsLoadingReturned] = useState(false);
    const [returnedSearch, setReturnedSearch] = useState('');
    const [returnedPage, setReturnedPage] = useState(1);
    const [returnedTotalPages, setReturnedTotalPages] = useState(0);
    const [returnedTotalRecords, setReturnedTotalRecords] = useState(0);

    // Dashboard stats for badges
    const [dashboardStats, setDashboardStats] = useState<AuthorDashboardStats | null>(null);

    const [initialized, setInitialized] = useState(false);
    const [hasInitialFetch, setHasInitialFetch] = useState(false);

    const fetchSubmissions = useCallback(async (cycleIdOverride?: number) => {
        if (!user?.id) return;
        const cycleId = cycleIdOverride !== undefined ? cycleIdOverride : (activeCycleId || 0);

        if (!cycleId || cycleId === 0) {
        return;
        }

        setIsLoading(true);
        try {
        const payload = {
            pageNumber,
            pageSize,
            sortBy: "",
            sortDescending: true,
            search: searchTerm,
            categoryId: 0,
            cycleId: cycleId,
            authorId: user.id,
            submittedFrom: null,
            submittedTo: null,
            applicationType: 0
        };

        const response = await authorAuthService.getSubmissions(payload);
        if (response.isSuccess && response.data) {
            setSubmissions(response.data.data);
            setTotalRecords(response.data.totalRecords);
            setTotalPages(response.data.totalPages);
        }
        } catch (error) {
        } finally {
        setIsLoading(false);
        }
    }, [user?.id, pageNumber, pageSize, searchTerm, activeCycleId]);

    const fetchReturnedSubmissions = useCallback(async () => {
        setIsLoadingReturned(true);
        try {
            const response = await authorAuthService.getSubmissionsByWorkflowStage({
                pageNumber: returnedPage,
                pageSize: pageSize,
                sortBy: '',
                sortDescending: true,
            });
            if (response.isSuccess && response.data) {
                setReturnedSubmissions(response.data.data);
                setReturnedTotalRecords(response.data.totalRecords);
                setReturnedTotalPages(response.data.totalPages);
            }
        } catch (error) {
        } finally {
            setIsLoadingReturned(false);
        }
    }, [returnedPage, pageSize]);

    const fetchDashboardStats = useCallback(async () => {
        try {
            const response = await authorAuthService.getAuthorDashboardStatistics();
            if (response.isSuccess && response.data) setDashboardStats(response.data);
        } catch (error) {
            // The table refresh still succeeds if the badge statistics cannot be reloaded.
        }
    }, []);

    // Fetch active cycle on mount
    useEffect(() => {
        const fetchActiveCycle = async () => {
        try {
            const response = await authorAuthService.getRunningCycle();
            if (response.isSuccess && response.data) {
            setActiveCycleId(response.data.id);
            // Initial fetch with non-zero cycle
            await fetchSubmissions(response.data.id);
            setHasInitialFetch(true);
            fetchDashboardStats();
            } else {
            setHasInitialFetch(true);
            }
        } catch (error) {
            setHasInitialFetch(true);
        } finally {
            setInitialized(true);
        }
        };
        fetchActiveCycle();
    }, [fetchDashboardStats, fetchSubmissions]);

    useEffect(() => {
        if (!initialized) return;
        if (activeCycleId && !hasInitialFetch) return;

        const timer = setTimeout(() => {
        fetchSubmissions();
        }, 500); // Debounce search

        return () => clearTimeout(timer);
    }, [fetchSubmissions, initialized, hasInitialFetch, activeCycleId]);

    useEffect(() => {
        if (activeTab === 'returned') fetchReturnedSubmissions();
    }, [activeTab, fetchReturnedSubmissions]);


    const toggleDrawer = () => {
        setIsDrawerOpen(!isDrawerOpen);
        if (isDrawerOpen) setSelectedSubmission(null);
    }

    const handleViewSubmission = (submission: SubmissionItem) => {
        setSelectedSubmission(submission);
        setIsDrawerOpen(true);
    };

    const handleReturnedResubmit = (row: any) => {
        setSelectedReturnedSubmission(row);
        setIsReturnedDrawerOpen(true);
    };

    const handleRevisedCopySuccess = () => {
        setIsReturnedDrawerOpen(false);
        setSelectedReturnedSubmission(null);
        void Promise.all([
            fetchReturnedSubmissions(),
            fetchDashboardStats(),
            fetchSubmissions(),
        ]);
    };

    const getStatusBadge = (status: string | number) => {
        const s = String(status).toLowerCase().replace(/\s/g, '');

        switch (s) {
        case 'approved':
        case '5':
            return <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">Approved</span>;
        case 'underreview':
        case '2':
            return <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">Under Review</span>;
        case 'revisionrequested':
        case '6':
        case 'submitrevisedcopy':
            return <span className="px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">Revision Requested</span>;
        case '8':
        case 'revisedcopysubmitted':
            return <span className="px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">Revised Copy Submitted</span>;
        case 'resubmit':
        case '7':
            return <span className="px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">Resubmit</span>;
        case 'rejected':
        case '4':
            return <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">Rejected</span>;
        case 'submitted':
        case '1':
            return <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">Submitted</span>;
        case 'returned':
        case '3':
            return <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">Returned</span>;
        default:
            const formatted = s.replace(/([A-Z])/g, ' $1').trim();
            return <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">{formatted}</span>;
        }
    };

    const getApplicationType = (type: number) => (type === 1 ? 'Book' : 'Journal Article');

    const columns: TableColumn<SubmissionItem>[] = [
        {
        key: 'id',
        label: 'S/N',
        render: (_value, _row, index) => <span className="font-medium text-gray-500">{(pageNumber - 1) * pageSize + (index ?? 0) + 1}</span>
        },
        {
        key: 'submissionNumber',
        label: 'Submission Number',
        render: (value) => <span className="font-medium text-gray-500">{value || 'N/A'}</span>
        },
        {
        key: 'applicationType' as any,
        label: 'Type',
        render: (_, row) => <span className="text-gray-700">{getApplicationType(row.applicationType)}</span>,
        },
        {
        key: 'title',
        label: 'Title',
        className: 'w-[16rem] max-w-[16rem] !whitespace-normal align-top',
        render: (value) => <span className="font-medium text-gray-900 break-words" title={value}>{value}</span>
        },
        {
        key: 'categoryName',
        label: 'Thematic Area',
        render: (_, row) => <span className="text-gray-500">{row.category?.name || row.categoryName}</span>
        },
        {
        key: 'status' as any,
        label: 'Status',
        className: 'w-[12rem] min-w-[12rem] whitespace-nowrap',
        render: (_, row) => getStatusBadge(row.status || row.statusId || '1')
        },
        {
        key: 'submittedAt',
        label: 'Submitted',
        render: (value) => <span className="text-gray-500">{new Date(value).toLocaleDateString()}</span>
        }
    ];

    const getActions = (row: SubmissionItem): TableAction<SubmissionItem>[] => [
        {
            icon: <Eye size={18} />,
            title: "View Details",
            onClick: (r) => handleViewSubmission(r),
            className: "text-gray-400 hover:text-gray-900 transition-colors p-1"
        }
    ];

    const returnedColumns: TableColumn<SubmissionItem>[] = [
        {
            key: 'id',
            label: 'S/N',
            render: (_value, _row, index) => <span className="font-medium text-gray-500">{(returnedPage - 1) * pageSize + (index ?? 0) + 1}</span>
        },
        {
            key: 'submissionNumber',
            label: 'Submission Number',
            render: (value) => <span className="font-medium text-gray-500">{value || 'N/A'}</span>
        },
        {
            key: 'applicationType',
            label: 'Type',
            render: (_, row) => <span className="text-gray-700">{getApplicationType(row.applicationType)}</span>,
        },
        {
            key: 'title',
            label: 'Title',
            render: (value) => <span className="font-medium text-gray-900 line-clamp-1" title={value}>{value}</span>
        },
        {
            key: 'categoryName',
            label: 'Thematic Area',
            render: (_, row) => <span className="text-gray-500">{row.category?.name || row.categoryName}</span>
        },
        {
        key: 'status' as any,
        label: 'Status',
        render: (_, row) => getStatusBadge(row.status || row.statusId || '1')
        },
        {
            key: 'submittedAt',
            label: 'Submitted',
            render: (value) => <span className="text-gray-500">{new Date(value).toLocaleDateString()}</span>
        }
  ];

    return (
        <div className="flex flex-col gap-6">
            <div>
                <Link to="/author" className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors text-sm font-medium">
                    <ArrowLeft size={16} />
                    Back to Dashboard
                </Link>
            </div>

            <div>
                <h1 className="text-3xl font-normal text-gray-900 mb-2">My Submissions</h1>
                <p className="text-gray-500">Track and manage all your manuscript submissions</p>
            </div>

            {/* Tabs - Segmented Control Style */}
            <div className="bg-gray-100 p-1 rounded-xl w-full">
                <div className="flex gap-1 w-full">
                    {[
                        { key: 'my', label: 'My Submissions' },
                        {
                            key: 'returned',
                            label: 'Revised Copy Requests',
                            badge: dashboardStats?.pendingSubmissions && dashboardStats.pendingSubmissions > 0 ? dashboardStats.pendingSubmissions : null
                        },
                    ].map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key as any)}
                            className={`flex-1 relative px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 flex items-center justify-center gap-2 ${
                                activeTab === tab.key
                                    ? 'bg-white text-gray-900 shadow-sm ring-1 ring-black/5'
                                    : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                            }`}
                        >
                            {tab.label}
                            {tab.badge != null && (
                                <span className={`inline-flex items-center justify-center px-1.5 py-0.5 rounded-full text-xs font-semibold transition-colors ${
                                    activeTab === tab.key
                                        ? 'bg-orange-500 text-white'
                                        : 'bg-orange-100 text-orange-700'
                                }`}>
                                    {tab.badge}
                                </span>
                            )}
                        </button>
                    ))}
                </div>
            </div>

                {/* My Submissions Tab */}
                {activeTab === 'my' && (
                    <>
                        <ReusableTable
                            columns={columns}
                            data={submissions}
                            getActions={getActions}
                            loading={isLoading}
                            searchPlaceholder="Search by title or ID..."
                            onSearch={(val) => { setSearchTerm(val); setPageNumber(1); }}
                            title="All Submissions"
                            subTitle={`${totalRecords} manuscripts found`}
                            showSearch={true}
                            showFilter={false}
                            emptyMessage="No submissions found."
                        />
                        {totalRecords > 0 && (
                            <div className="flex flex-col gap-4 px-6 py-4 border-t border-gray-100 bg-gray-50/50 rounded-b-xl -mt-2 mx-0.5 border-x border-b sm:flex-row sm:items-center sm:justify-between">
                                <div className="flex items-center gap-2 text-sm text-gray-500">
                                    <span>Show</span>
                                    <select
                                        value={pageSize}
                                        onChange={(event) => {
                                            setPageSize(Number(event.target.value));
                                            setPageNumber(1);
                                        }}
                                        className="rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#088A61]"
                                    >
                                        {[20, 50, 75, 100].map(size => <option key={size} value={size}>{size}</option>)}
                                    </select>
                                    <span>per page</span>
                                </div>
                                <div className="flex flex-wrap items-center gap-2">
                                    <button onClick={() => setPageNumber(prev => Math.max(prev - 1, 1))} disabled={pageNumber === 1} className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">Previous</button>
                                    {getVisiblePageNumbers(pageNumber, totalPages).map((page, index) =>
                                        page === 'ellipsis' ? (
                                            <span key={`ellipsis-${index}`} className="px-1 text-sm text-gray-400">…</span>
                                        ) : (
                                            <button
                                                key={page}
                                                onClick={() => setPageNumber(page)}
                                                className={`min-w-9 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${page === pageNumber ? 'bg-[#088A61] text-white' : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}
                                            >
                                                {page}
                                            </button>
                                        )
                                    )}
                                    <button onClick={() => setPageNumber(prev => Math.min(prev + 1, totalPages))} disabled={pageNumber === totalPages} className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">Next</button>
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* Returned Submissions Tab */}
                {activeTab === 'returned' && (
                    <>
                        <ReusableTable
                            columns={returnedColumns}
                            data={returnedSubmissions.filter(s =>
                                !returnedSearch ||
                                s.title?.toLowerCase().includes(returnedSearch.toLowerCase()) ||
                                s.submissionNumber?.toLowerCase().includes(returnedSearch.toLowerCase())
                            )}
                            getActions={(row) => [{
                                icon: <Upload size={18} />,
                                title: 'Submit Revised Copy',
                                onClick: (r) => handleReturnedResubmit(r),
                                className: 'text-blue-400 hover:text-blue-900 transition-colors p-1'
                            }]}
                            loading={isLoadingReturned}
                            searchPlaceholder="Search by title or submission number..."
                            onSearch={(val) => { setReturnedSearch(val); setReturnedPage(1); }}
                            title="Returned Submissions"
                            subTitle={`${returnedTotalRecords} submissions found`}
                            showSearch={true}
                            showFilter={false}
                            emptyMessage="No returned submissions found."
                        />
                        {returnedTotalPages > 1 && (
                            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50/50 rounded-b-xl -mt-2 mx-0.5 border-x border-b">
                                <p className="text-sm text-gray-500">
                                    Page <span className="font-medium text-gray-900">{returnedPage}</span> of <span className="font-medium text-gray-900">{returnedTotalPages}</span>
                                </p>
                                <div className="flex gap-2">
                                    <button onClick={() => setReturnedPage(prev => Math.max(prev - 1, 1))} disabled={returnedPage === 1} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">Previous</button>
                                    <button onClick={() => setReturnedPage(prev => Math.min(prev + 1, returnedTotalPages))} disabled={returnedPage === returnedTotalPages} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">Next</button>
                                </div>
                            </div>
                        )}
                    </>
                )}

                <Drawer isOpen={isDrawerOpen} onClose={toggleDrawer} title="Submission Details">
                    {selectedSubmission && <SubmissionDetails submissionId={selectedSubmission.id} />}
                </Drawer>

                <Drawer
                    isOpen={isReturnedDrawerOpen}
                    onClose={() => { setIsReturnedDrawerOpen(false); setSelectedReturnedSubmission(null); }}
                    title="Submit Revised Copy"
                >
                    {selectedReturnedSubmission && (
                        <RevisedCopyDetails
                            submissionId={selectedReturnedSubmission.id}
                            onSuccess={handleRevisedCopySuccess}
                        />
                    )}
                </Drawer>
        </div>
    );
};

export default Submissions;
